import React, { useRef, useState, useEffect } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter } from "reactstrap";
import { Autocomplete, GoogleMap, Marker, useJsApiLoader } from "@react-google-maps/api";

import CardLoader from "../components/CardLoader";

import { useDispatch } from "react-redux";
import * as inspectorActions from "../actions/InspectorActions";
import * as commonActions from "../actions/CommonActions";

const libraries = ["places"];

const GoogleMapModal = ({ address, latitude, longitude, show, onYesClick, onCloseClick }) => {
  const containerStyle = {
    width: "100%",
    height: "600px",
  };

  const dispatch = useDispatch();

  const mapRef = useRef(null);
  const acRef = React.useRef(null);
  const acInputRef = React.useRef(null);

  const [currentLocationCoordinate, setCurrentLocationCoordinate] = useState(null);
  const [currentLocationData, setCurrentLocationData] = useState(null);
  const [loadingLocal, setLoadingLocal] = React.useState(false);

  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: "AIzaSyDhX7dXkCp3tNyvU7lO1GflUd76j-0tbWM",
    libraries
  });

  const reverseGeocodeByCoordinate = (lat, lng) => {
    return new Promise((resolve, reject) => {
      const geocoder = new window.google.maps.Geocoder();

      geocoder.geocode(
        { location: { lat, lng } },
        (results, status) => {
          if (status === "OK" && results[0]) {
            resolve(results[0]);
          } else {
            resolve(null);
          }
        }
      );
    });
  };

  const reverseGeocodeByAddress = (address) => {
    return new Promise((resolve, reject) => {
      const geocoder = new window.google.maps.Geocoder();

      geocoder.geocode(
        { address },
        (results, status) => {
          if (status === "OK" && results[0]) {
            resolve(results[0]);
          } else {
            resolve(null);
          }
        }
      );
    });
  };

  const setDefaultCurrentLocation = () => {
    if (!navigator.geolocation) return;

    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        setCurrentLocationCoordinate({ 
          lat: pos.coords.latitude
          , lng: pos.coords.longitude
          , isDefault: true
        });
      },
      (err) => {
        console.warn("Location permission denied", err);
      },
      {
        enableHighAccuracy: true,
        timeout: 8000,
        maximumAge: 0,
      }
    );
  };

  const isGoogleMapsLink = (value) => {
    return value.includes("maps.app.goo.gl") || value.includes("google.com/maps");
  }

  const isLatLng = (value) => {
    return /^-?\d+(\.\d+)?\s*,\s*-?\d+(\.\d+)?$/.test(value);
  }

  const parseLatLng = (value) => {
    const [lat, lng] = value.split(",").map(v => parseFloat(v.trim()));
    return { lat, lng };
  };

  const parseCurrentLocationInfo = (place) => {
    if(acInputRef && acInputRef.current) {
      acInputRef.current.value = place.formatted_address;
    }

    const addressComponents = place.address_components;
    const getComponent = (type) => addressComponents.find(c => c.types.includes(type))?.long_name || "";
    
    var locationData = {
      address: place.formatted_address
      , country: getComponent("country")
      , city: getComponent("locality")
      , state: getComponent("administrative_area_level_1")
      , zipCode: getComponent("postal_code")
      , latitude: place.geometry.location.lat()
      , longitude: place.geometry.location.lng()
    };

    
    setCurrentLocationCoordinate({ 
      lat: locationData.latitude
      , lng: locationData.longitude
      , isDefault: false
    });

    setCurrentLocationData(locationData);
  }

  useEffect(() => {
    if (!mapRef.current || !currentLocationCoordinate || !show) {
      return;
    }

    const timer = setTimeout(() => {
      //window.google.maps.event.trigger(mapRef.current, "resize");
      mapRef.current.panTo(currentLocationCoordinate);
      mapRef.current.setZoom(15);

      if(currentLocationData && acInputRef && acInputRef.current) {
        acInputRef.current.value = currentLocationData.address;
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [currentLocationCoordinate, show]);

  useEffect(() => {
    async function processAddress() {
      if(!isLoaded) {
        return;
      }

      if(latitude && longitude) {
        setCurrentLocationCoordinate({ 
          lat: latitude
          , lng: longitude
          , isDefault: false
        });
        
        return;
      }

      if(address) {
        var place = await reverseGeocodeByAddress(address);
        if(place) {
          parseCurrentLocationInfo(place);
        }

        return;
      }

      if(!address) {
        setDefaultCurrentLocation();
        return;
      }
    }

    processAddress();
  }, [isLoaded, address, latitude, longitude]);

  const handleMapClick = async (e) => {
    setLoadingLocal(true);
    
    const place = await reverseGeocodeByCoordinate(e.latLng.lat(), e.latLng.lng());
    if(place) {
      parseCurrentLocationInfo(place);
    }

    setLoadingLocal(false);
  };

  const handlePlaceChanged = () => {
    const place = acRef.current.getPlace();
    
    if (!place.geometry) {
      return;
    }

    parseCurrentLocationInfo(place);
  }

  const handleBlur = async () => {
    const value = acInputRef.current.value.trim();

    if(value) {
      try {
        setLoadingLocal(true);

        var place = null;

        if (isLatLng(value)) {
          const { lat, lng } = parseLatLng(value);
          place = await reverseGeocodeByCoordinate(lat, lng);
        }

        if (isGoogleMapsLink(value)) {
          var result = await inspectorActions.resolveGoogleMapLink(value); // backend
          if(result.error) {
            dispatch(commonActions.setErrorMessage(result.error));
          }
          else {
            place = await reverseGeocodeByCoordinate(result.latitude, result.longitude);
          }
        }

        if(place) {
          parseCurrentLocationInfo(place);
        }
      }
      catch(error) {
        dispatch(commonActions.setErrorMessage("Unable to Resolve Location"));
      }
      finally {
        setLoadingLocal(false);
      }
    }
  };

  const onAddressSelect = () => {
    if(onYesClick && currentLocationData) {
      onYesClick(currentLocationData);
    } 
  }

  if (!isLoaded) {
    return(
      <div>Loading...</div>
    );
  }

  return (
    <Modal size={"xl"} isOpen={show} toggle={onCloseClick} centered={true}>
      <CardLoader loading={loadingLocal} />
      <ModalHeader className="d-flex justify-content-center">
        Location Map
      </ModalHeader>
      <ModalBody className="h5 pb-3 text-center">
        <div className="mb-2">
          <Autocomplete
              onLoad={(ac) => (acRef.current = ac)}
              onPlaceChanged={handlePlaceChanged}
              options={{ strictBounds: false }}
              >
              <input
                  ref={acInputRef}
                  type="text"
                  className="form-control"
                  autoComplete="off"
                  placeholder="Search address or paste Google Maps link or paste lat/lng"
                  onBlur={handleBlur}
                  onPaste={(e) => {
                    setTimeout(() => {
                      acInputRef.current?.blur();
                    }, 100);
                  }}
              />
          </Autocomplete>
        </div>
        <GoogleMap
          mapContainerStyle={containerStyle}
          center={currentLocationCoordinate || { lat: 31.5204, lng: 74.3587 }}
          zoom={14}
          onLoad={(map) => (mapRef.current = map)}
          onClick={handleMapClick}
        >
          {currentLocationCoordinate && !currentLocationCoordinate.isDefault && (
            <Marker
              position={currentLocationCoordinate}
              draggable
              onDragEnd={handleMapClick} // <-- DRAG SUPPORT
            />
          )}
        </GoogleMap>
      </ModalBody>
      <ModalFooter className="d-flex justify-content-center">
        {currentLocationCoordinate && currentLocationData &&
        <button type="button" className="btn btn-dark btn-label w-md" data-bs-dismiss="modal" onClick={onAddressSelect}>
          <i className="ri-close-line label-icon align-bottom"></i> Use This Location
        </button>
        }
        <button type="button" className="btn btn-dark btn-label w-md" data-bs-dismiss="modal" onClick={onCloseClick}>
          <i className="ri-close-line label-icon align-bottom"></i> Close
        </button>
      </ModalFooter>
    </Modal>
  );
};

export default GoogleMapModal;